/**
 * カードから開くポップオーバーの背景。**テーマの `MuiPopover`(0.75) に対する意図的な例外。**
 *
 * 【なぜ例外にするか】
 * メニュー面の標準は `theme.ts` の `MuiMenu` / `MuiPopover`（`rgba(0,0,0,0.75)`）で、
 * それはマイページのバナーやヘッダーのように**ほぼ無地の上に出る**前提で足りている。
 * 一方こちらはカバー画像が敷き詰まったカードグリッドの上に開くので、
 * 同じ 0.75 だと下の絵が透けて文字が読みにくい。濃度だけを上げる。
 *
 * 【値を増やさないこと】
 * `0.85` はプロジェクトに既にある濃い側の値（`MemoEditorDialog` / `MemoPreviewDialog` /
 * `GameReviewPreviewDialog` / `HubQuickSubmitDialog`）。新しい中間値を作ると、
 * 以前 0.7 を発明して「揃えた」と言ってしまったのと同じ状態に戻る。
 *
 * 【触るのは「黒の濃さ」と「白の量」の2つだけにすること】
 * ぼかし・枠・角丸はテーマから継承させる。ここで面を丸ごと再定義すると、
 * テーマを直しても追従しない箇所がまた生まれる。
 *
 * 適用先: `GameCardStatusMenu` / `FolderPickerPopover` / `GameListPickerPopover`
 * （＝カードのステータスメニューとそこから連続して開く2つのピッカー）。
 * この3つは全部このオブジェクトを spread しているので、**調整はこのファイルだけで済む。**
 * `AnimeCard` / `MovieCard` は汎用 `PopoverMenu` を使っており未適用。
 */

/**
 * Paper の elevation オーバーレイ（白）をどこまで残すか。**濃さの調整はこの数値でやること。**
 *
 * MUI はダークモードで白のグラデーションを `backgroundImage` として乗せてくる
 * （詳細は `theme.ts` 冒頭の TODO コメント）。Popover の既定 elevation は 8 で、
 * そのとき乗る量は **0.119**。それをここで任意の量に差し替える。
 *
 *   0      … 完全に無し（指定した backgroundColor がそのまま出る＝一番濃い）
 *   0.05   … 既定の半分弱
 *   0.119  … MUI 既定と同じ＝実質何もしないのと同じ
 *
 * `backgroundColor` 側（0.85）を動かすより、こちらで詰めたほうが意図が読みやすい。
 * 黒の濃さは「どれだけ下地を隠すか」、この白は「面をどれだけ持ち上げるか」で別の話なので。
 */
const OVERLAY_ALPHA = 0.095;

export const CARD_POPOVER_BG_SX = {
  backgroundColor: 'rgba(0, 0, 0, 0.85)',
  /**
   * **`--Paper-overlay` ではなく `backgroundImage` を上書きすること。**
   * MUI はあの CSS 変数を `style` 属性（インライン）で要素に直接置いているため、
   * sx から変数を書き換えても勝てない。クラス側にあるのは
   * `backgroundImage: var(--Paper-overlay)` なので、こちらを差し替えれば効く。
   */
  backgroundImage: `linear-gradient(rgba(255, 255, 255, ${OVERLAY_ALPHA}), rgba(255, 255, 255, ${OVERLAY_ALPHA}))`,
} as const;
